'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import { RotateCcwIcon } from 'lucide-react';
import { useStopwatch } from 'react-timer-hook';
import { useLocalStorage } from 'react-use';

import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { SearchAndFindGameVersions } from '~/types/games';
import { clamp, getRandomInt, getRandomIntInclusive } from '~/utils/number';

import { GameHighScore } from '../../_components/game-high-score';
import GameResultDialog from '../../_components/game-result-dialog';
import { GameScore } from '../../_components/game-score';
import { GameTimer } from '../../_components/game-timer';
import { SearchAndFindGameBoardSkeleton } from './search-and-find-game-board';
import { GameLevels } from './search-and-find-game-levels';

const SearchAndFindGameBoard = dynamic(
  () => import('./search-and-find-game-board').then((mod) => mod.SearchAndFindGameBoard),
  { ssr: false, loading: () => <SearchAndFindGameBoardSkeleton /> },
);

const games = {
  default: ['controller', 'guitar', 'duck', 'cupcake', 'lollipop'],
  princess: ['bird', 'teapot', 'shell', 'magic-lamp', 'egg'],
  dinosaurs: ['brontosaurus', 'pterodactyl', 'stegosaurus', 'triceratops', 't-rex', 'ankylosaurus', 't-rex-green'],
};

const symbolAlts = 2;

const rotations = [
  [336, 268, 6, 40, 235, 82, 74, 272, 356, 271, 69, 64],
  [246, 336, 275, 343, 276, 53, 278, 298, 115, 0, 335, 259],
];
const secondsPerRound = 2.5;
const timeScoreWeight = 0.3;

interface SearchAndFindGameProps {
  version?: SearchAndFindGameVersions;
}

export default function SearchAndFindGame({ version = 'default' }: SearchAndFindGameProps) {
  const levels = games[version];
  const storageKeyPrefix = `${version === 'default' ? '' : `${version}-`}`;

  const [moves, setMoves] = React.useState<number>(0);
  const [level, setLevel] = React.useState<number>(0);
  const [altSymbolIdx, setAltSymbolIdx] = React.useState(() => getRandomIntInclusive(1, symbolAlts));
  const [rotationsIdx, setRotationsIdx] = React.useState(() => getRandomInt(0, rotations.length));
  const [finished, setFinished] = React.useState<boolean>(false);
  const [newHighScore, setNewHighScore] = React.useState<boolean>(false);
  const [resultsDialogOpen, setResultsDialogOpen] = React.useState<boolean>(false);

  const [bestScore, setBestScore] = useLocalStorage<number>(`${storageKeyPrefix}find-game-best-score`);
  const [bestMoves, setBestMoves] = useLocalStorage<number>(`${storageKeyPrefix}find-game-best-moves`);
  const [bestTime, setBestTime] = useLocalStorage<number>(`${storageKeyPrefix}find-game-best-time`);

  const { seconds, minutes, pause, start, reset } = useStopwatch({ autoStart: true });
  const totalSeconds = React.useMemo(() => Math.floor(minutes * 60 + seconds), [seconds, minutes]);

  const timeScore = React.useMemo(() => Math.ceil(totalSeconds - secondsPerRound * (level + 1)), [level, totalSeconds]);
  const movesScore = React.useMemo(() => moves - (level + 1), [level, moves]);
  const totalScore = React.useMemo(
    () => clamp(0, Math.floor((100 - (timeScore * timeScoreWeight + movesScore)) * ((level + 1) / levels.length)), 100),
    [timeScore, movesScore, level, levels.length],
  );

  const nextLevel = React.useCallback(() => {
    setResultsDialogOpen(false);
    setLevel(level + 1);
    start();
  }, [level, start]);

  const restartGame = React.useCallback(() => {
    setResultsDialogOpen(false);
    setLevel(0);
    setMoves(0);
    setFinished(false);
    setNewHighScore(false);
    reset();
  }, [reset]);

  const finishGame = React.useCallback(() => {
    pause();

    if (level + 1 === levels.length) {
      const bests = {
        score: bestScore ?? 0,
        moves: bestMoves ?? Number.MAX_SAFE_INTEGER,
        time: bestTime ?? Number.MAX_SAFE_INTEGER,
      };

      // Update best score, tie-break
      if (
        totalScore > bests.score ||
        (totalScore === bests.score && moves < bests.moves) ||
        (totalScore === bests.score && moves === bests.moves && totalSeconds < bests.time)
      ) {
        setBestMoves(moves);
        setBestTime(totalSeconds);
        setBestScore(totalScore);
        setNewHighScore(true);
      }

      setFinished(true);
    }

    setTimeout(() => setResultsDialogOpen(true), 400);
  }, [
    bestMoves,
    bestScore,
    bestTime,
    level,
    levels.length,
    moves,
    pause,
    setBestMoves,
    setBestScore,
    setBestTime,
    totalScore,
    totalSeconds,
  ]);

  React.useEffect(() => {
    setAltSymbolIdx(getRandomIntInclusive(1, symbolAlts));
    setRotationsIdx((prev) => (prev + 1 >= rotations.length ? 0 : prev + 1));
  }, [level]);

  const headerContent = React.useMemo(
    () => (
      <svg className='h-full w-full' fill='none' focusable='false' role='img' xmlns='http://www.w3.org/2000/svg'>
        <use xlinkHref={`#${levels[level]}-${altSymbolIdx}`} />
      </svg>
    ),
    [altSymbolIdx, level, levels],
  );

  return (
    <div className='flex flex-col items-center px-8'>
      <SearchAndFindGameBoard
        finishGame={finishGame}
        key={levels[level]}
        rotations={rotations[rotationsIdx]}
        setMoves={setMoves}
        symbol={levels[level]}
        uniqueSymbol={`${levels[level]}-${altSymbolIdx}`}
      />
      <GameTimer moves={moves} seconds={totalSeconds} />
      <GameResultDialog
        title={newHighScore ? (totalScore === 100 ? 'A Perfect Score!' : 'New Best Score!') : 'Awesome Job!'}
        headerContent={headerContent}
        open={resultsDialogOpen}
        setOpen={setResultsDialogOpen}
        showConfetti={finished && resultsDialogOpen}
        confettiAmount={newHighScore || totalScore === 100 ? 'excessive' : 'default'}
      >
        <GameScore moves={moves} score={totalScore} seconds={totalSeconds} />
        <GameLevels currentLevelIdx={level} levels={levels} />
        {level + 1 < levels.length ? (
          <Button className='mt-6 w-full' onClick={nextLevel}>
            Play level {level + 2}
          </Button>
        ) : (
          <>
            {bestMoves != null && bestScore != null && bestTime != null && (
              <GameHighScore moves={bestMoves} score={bestScore} seconds={bestTime} />
            )}
            <div className='mt-6 flex flex-wrap gap-3'>
              <Button className='flex-1' onClick={restartGame}>
                <RotateCcwIcon />
                Play Again
              </Button>
              <Button className='flex-1' variant='outline-secondary' asChild>
                <Link href='/games'>Back to Games</Link>
              </Button>
            </div>
          </>
        )}
      </GameResultDialog>
    </div>
  );
}
